<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import Star from '$lib/components/Star.svelte';
  import ComparisonTable from '$lib/components/ComparisonTable.svelte';
  import SectionNav from '$lib/components/SectionNav.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';

  const navSections = [
    { id: 'the-problem', label: 'The Problem' },
    { id: 'the-thesis', label: 'The Thesis' },
    { id: 'three-paths', label: 'Three Paths' },
    { id: 'for-the-engineer', label: 'For the Engineer' },
    { id: 'sovereignty', label: 'Sovereignty' },
  ];

  const comparisonRows = [
    { label: 'Cost', si: '$500K – $2M+', internal: '$500K – $1M+ in eng time', corral: 'Platform subscription' },
    { label: 'Timeline', si: '6 – 12 months', internal: '6 – 12 months', corral: '30 minutes to deploy' },
    { label: 'Outcome', si: 'Someone else\'s vision of your AI, at $500K+.', internal: '6-12 months before you can start making it yours.', corral: 'Start shaping AI to your business on day one.' },
    { label: 'Maintenance', si: 'Back to the SI every time something changes.', internal: 'Your team maintains it forever.', corral: 'Managed. Updates ship continuously.' },
    { label: 'Security', si: 'Bolted on. Varies by engagement.', internal: 'Only as good as your team\'s security expertise.', corral: 'Built into the architecture. CRF governs across every layer.' },
    { label: 'Extensibility', si: 'Locked into the SI\'s architecture.', internal: 'Fully custom, but fully your burden.', corral: 'API-first. MCP/OpenAPI. Override anything.' },
    { label: 'Week 2', si: 'Still in procurement.', internal: 'Still building auth.', corral: 'Building agents for your workflows.' },
  ];

  const sectors = [
    { label: 'Healthcare', detail: 'HIPAA, patient data residency, PHI protection' },
    { label: 'Government & Education', detail: 'FedRAMP, data sovereignty, public sector procurement requirements' },
    { label: 'Financial Services', detail: 'Regulatory data handling, audit trail requirements, cross-border data restrictions' },
    { label: 'EU-based Enterprises', detail: 'GDPR, EU AI Act, digital sovereignty mandates' },
    { label: 'Contractual', detail: 'Any organization with data residency clauses' },
  ];
</script>

<PageMeta
  title="Why Corral — Your AI should work the way your business works"
  description="Enterprise AI projects fail at 50%. Not because the AI doesn't work — because getting it to work your way costs $500K and takes a year. See why Corral is the third option."
/>

<!-- Top stars at grid line positions -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <div class="flex flex-col items-center text-center max-w-[700px] mx-auto">
      <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
        Why Corral
      </h1>
      <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
        The complete AI foundation — deployed into your Azure tenant, maintained by us, shaped by you. Here's why that matters.
      </p>
    </div>
  </GridContent>
</section>

<!-- Page Divider: Hero → The Problem -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<SectionNav sections={navSections} />

<!-- §1: The Problem -->
<section id="the-problem" class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <h2 class="font-sans text-[48px] lg:text-[58px] text-white tracking-[-0.04em] leading-[1.1] mb-8">
          Enterprise AI has a deployment problem. Not a capability problem.
        </h2>
        <div class="flex flex-col gap-6">
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            The models work. GPT-4, Claude, Gemini, Llama — intelligence is commodity. You can have a working AI prototype in an afternoon.
          </p>
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            Getting it into production — shaped to how your business actually works, inside your compliance boundary, connected to your systems? That takes 6-12 months and costs $500K+. Not because the AI is hard. Because the system around it is hard: authentication, model orchestration, guardrails, tool integration, user interfaces, governance, compliance, deployment architecture. Seven layers, all interdependent. Skip any one and you're stuck building infrastructure instead of building your AI.
          </p>
          <div class="my-4 border-l-2 border-white/20 pl-6 py-1">
            <p class="font-body text-[18px] text-white leading-relaxed">
              50% of enterprise AI projects fail. Not because the AI doesn't work — because the system around it is incomplete, so nobody ever gets to the part where AI delivers value for their specific business.
            </p>
          </div>
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            The model is an API key away. That was never the hard part.
          </p>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Page Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
  <Star variant="full" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="full" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §2: The Thesis -->
<section id="the-thesis" class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <h2 class="font-sans text-[48px] lg:text-[58px] text-white tracking-[-0.04em] leading-[1.1] mb-8">
          Your AI should work the way your business works. Getting there shouldn't cost $500K.
        </h2>
        <div class="flex flex-col gap-6">
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            A model gateway doesn't ship AI. A guardrails layer doesn't ship AI. A chat widget doesn't ship AI. What ships AI is the complete system: models connected to tools connected to data, surfaced through interfaces people actually use, governed by controls IT actually trusts, deployed in an architecture that passes compliance. And then shaped to how your business actually operates.
          </p>
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            You can't customize something that's half-built. You're too busy finishing the foundation to start shaping AI to your workflows, your data, your business logic. The complete stack is what creates the surface area for making it yours.
          </p>
        </div>

        <!-- Pull quote -->
        <div class="my-10 border-l-2 border-[#ee6018] pl-6 py-2">
          <p class="font-mono font-light text-[18px] text-white leading-relaxed">
            Anyone can build a piece of this. The complete, cohesive, maintained system — that's what's hard. That's what costs $500K and takes a year. And that's what Corral ships in 30 minutes from Azure Marketplace — so you can start making it yours on day one, not month six.
          </p>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Page Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative">
  <GridLines variant="light" />
</div>

<!-- §3: Three Paths -->
<section id="three-paths" class="bg-[#f9f9f4] py-[80px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <!-- Header -->
      <div class="flex flex-col items-center text-center mb-12">
        <h2 class="font-sans text-[48px] lg:text-[58px] leading-[1.1] tracking-[-0.04em] text-[#020202] mb-5">
          Three ways to make AI yours. Only one that scales.
        </h2>
      </div>

      <!-- Comparison table -->
      <div class="relative">
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="bg-white border border-[#D3D3D3] p-6 lg:p-8 relative">
          <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute -left-[10px] -top-[1px]" />
          <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute -right-[10px] -top-[1px]" />
          <Star variant="top" color="#D3D3D3" class="hidden lg:block absolute -left-[10px] -bottom-px" />
          <Star variant="top" color="#D3D3D3" class="hidden lg:block absolute -right-[10px] -bottom-px" />

          <ComparisonTable rows={comparisonRows} variant="light" />
        </div>
      </div>

      <!-- Body below table -->
      <div class="max-w-[700px] mx-auto mt-12">
        <p class="font-body font-light text-[16px] text-[#5C5855] leading-relaxed mb-4">
          If you have the team, the time, and the appetite to maintain a complete AI stack while the landscape shifts weekly — build it yourself. Seriously. Some companies should.
        </p>
        <p class="font-body font-light text-[16px] text-[#020202] leading-relaxed">
          But if you'd rather spend your engineering time on the AI that makes your business unique instead of rebuilding the same seven layers everyone else is rebuilding — that's what Corral is for. The foundation is ours. What you build on it is yours.
        </p>
        <a href="/company/contact" class="inline-flex items-center gap-1.5 font-mono text-[14px] text-[#5C5855] hover:text-[#ee6018] transition-colors mt-8">
          Ready to see it? Get in touch <span aria-hidden="true">→</span>
        </a>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Page Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §4: For the Engineer -->
<section id="for-the-engineer" class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <h2 class="font-sans text-[48px] lg:text-[58px] text-white tracking-[-0.04em] leading-[1.1] mb-8">
          You've been building this yourself. We know. So have we.
        </h2>
        <div class="flex flex-col gap-6">
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            You're the engineer (or the team of one) who's been wiring together auth, model routing, guardrails, a chat UI, an admin console, observability, and deployment infrastructure. You're good at it. And 80% of your time goes to plumbing that every company rebuilds from scratch.
          </p>
          <p class="font-body font-light text-[16px] text-white leading-relaxed">
            Corral doesn't replace you. It gives you your time back. Every hour you spend on the foundation is an hour you're not spending on the AI that makes your company unique.
          </p>
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            The platform is extensible down to the code level — API-first, MCP and OpenAPI extension points, replaceable UI components. Build on top of it. Override pieces of it. Extend it with your own business logic, custom tools, and integrations. You're not locked out. You're unlocked.
          </p>
          <div class="mt-4 border-l-2 border-white/20 pl-6 py-1">
            <p class="font-body text-[18px] text-white leading-relaxed">
              On day one, an internal build feels like ownership. On day 90, it feels like maintenance. Corral's ownership is durable — you keep shaping your AI while the foundation stays maintained underneath.
            </p>
          </div>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Page Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
  <Star variant="full" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="full" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §5: Sovereignty & Compliance -->
<section id="sovereignty" class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <!-- Header -->
      <div class="flex flex-col items-center text-center mb-12 max-w-[700px] mx-auto">
        <h2 class="font-sans text-[48px] lg:text-[58px] text-white tracking-[-0.04em] leading-[1.1] mb-5">
          Control isn't optional. It's becoming law.
        </h2>
        <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
          GDPR. The EU AI Act. HIPAA. FedRAMP. Sector-specific regulations in healthcare, finance, government, and education. Data residency requirements that vary by region, contract, and jurisdiction. This isn't slowing down. The EU is actively pushing for technology sovereignty. Global volatility is accelerating the demand for infrastructure that doesn't depend on a foreign vendor's cloud.
        </p>
      </div>

      <!-- Sector cards -->
      <div class="relative">
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#333335] border border-[#333335]">
          {#each sectors as sector}
            <div class="bg-[#171719] p-6">
              <h3 class="font-sans text-[16px] text-white mb-2">{sector.label}</h3>
              <p class="font-mono font-light text-[14px] text-[#878787] leading-relaxed">{sector.detail}</p>
            </div>
          {/each}
        </div>
      </div>

      <!-- Closing statement -->
      <div class="max-w-[700px] mx-auto mt-12 text-center">
        <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed mb-4">
          On-tenant deployment isn't just our architecture — it's the architecture that survives regulatory and geopolitical shifts. Your AI, your cloud, your jurisdiction. SaaS means you're renting someone else's AI. On-tenant means it's genuinely yours.
        </p>
        <p class="font-mono text-[15px] text-white">
          Your data never leaves your tenant. Not by policy — by architecture. We can't access it.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Page Divider: Sovereignty → CTA -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA Close -->
<ContactCtaSection />
